import { Component, OnInit } from '@angular/core';

import * as S3 from "aws-sdk/clients/s3";
import { UserSandbox } from '../../user.sandbox';
import { environment } from '../../../../environments/environment';


@Component({
  selector: 'app-my-files',
  templateUrl: './my-files.component.html',
  styleUrls: ['./my-files.component.css']
})
export class MyFilesComponent implements OnInit {

  private files;
  filesToShow = [];
  private bucket: S3;
  selectedFile;
  private projectName;
  loading;
  private keyToDelete;
  videoUrl;
  timelapseShown = false;
  breadcrumb: string[] = [];
  foldersToShow: string[] = [];

  constructor(private userSandbox: UserSandbox) { 
    
  }

  ngOnInit() {
    this.init();
  }

  init() {
    this.loading = true;
    this.bucket = new S3({
        accessKeyId: environment.awsAccessKeyId,
        secretAccessKey: environment.awsSecretAccessKey,
        region: 'eu-central-1'
    });
    this.userSandbox.getSelectedProject().subscribe((project) => {
      this.projectName = project.folder;
      this.breadcrumb = [this.projectName];
      const params = {
        Bucket: 'buildevolution-myfiles',
        Prefix: project.folder + '/'
      };
      this.bucket.listObjects(params, (err, data) => {
        this.files = data.Contents.filter(file => {
          return file.Key != this.projectName + '/'
        }).map(file => {
          file.Key = file.Key.replace(this.projectName + '/', '');
          return file;
        });
        this.updateFolderContents();
        this.loading = false;
        this.selectedFile = false;
      });
    });
  }

  updateFolderContents() {
    // Get the current path from breadcrumb
    const currentPath = this.breadcrumb.length > 1 
      ?  this.breadcrumb.slice(1).join('/') + '/'
      : '';

    // Find folders at the current level
    this.foldersToShow = Array.from(new Set(
      this.files
        .filter(file => {
          // Only consider files that start with the current path but go deeper
          if (!file.Key.startsWith(currentPath)) return false;
          
          // Skip files directly in this folder (no additional slashes)
          const remainingPath = file.Key.substring(currentPath.length);
          if (!remainingPath.includes('/')) return false;
          
          // Extract the next folder name
          return true;
        })
        .map(file => {
          const remainingPath = file.Key.substring(currentPath.length);
          return remainingPath.split('/')[0];
        })
    ));
    
    // Add Timelapse folder if at root and not already present
    // if (this.breadcrumb.length === 1 && !this.foldersToShow.includes('Timelapse')) {
    //   this.foldersToShow.unshift('Timelapse');
    // }
    
    // Find files at current level (no additional path segments)
    this.filesToShow = this.files.filter(file => {
      // If we're at root and in Timelapse folder
      if (this.breadcrumb.length === 2 && this.breadcrumb[1] === 'Timelapse') {
        return file.Key.startsWith('timelapse/');
      }
      
      if (!file.Key.startsWith(currentPath)) return false;
      const remainingPath = file.Key.substring(currentPath.length);
      return remainingPath.length > 0 && !remainingPath.includes('/');
    });
  }

  openFolder(folder: string) {
    // Add to breadcrumb navigation
    this.breadcrumb.push(folder);
    
    // Special handling for Timelapse folder
    if (folder === 'Timelapse' && this.breadcrumb.length === 2) {
      // List all timelapse files
      this.filesToShow = this.files.filter(file => 
        file.Key.startsWith('timelapse/') && !file.Key.endsWith('/')
      );
      this.foldersToShow = [];
    } else {
      // Normal folder navigation
      this.updateFolderContents();
    }
  }

  navigateToFolder(folder: string, index: number) {
    // Navigate to the selected folder in breadcrumb
    this.breadcrumb = this.breadcrumb.slice(0, index + 1);
    this.updateFolderContents();
  }

  async playVideo(key) {
    this.videoUrl = await this.bucket.getSignedUrl('getObject', {Bucket: "buildevolution-myfiles", Key: this.projectName + '/' + key, Expires: 60*5})
  }

  closeVideo() {
    this.videoUrl = null;
  }

  async downloadItem(key) {
    const itemURL = await this.bucket.getSignedUrl('getObject', {Bucket: "buildevolution-myfiles", Key: this.projectName + '/' + key, Expires: 60*5})
    const link = document.createElement('a')
    link.href = itemURL
    link.target= "_blank";
    link.download = 'buildevolution-file'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  upload() {
    this.loading = true;
    const file = this.selectedFile;
    const params = {
      Bucket: 'buildevolution-myfiles',
      Key: this.projectName + '/' + file.name,
      Body: file,
      ContentType: file.type
    }
    
    this.bucket.upload(params, (err, data) => {
      if (err) {
          console.log('There was an error uploading your file: ', err);
          return false;
      }
      this.init();
      return true;
    });
  }


  confirmDelete(key) {
    this.keyToDelete = key;
  }

  resetConfirmDelete() {
    this.keyToDelete = undefined;
  }

  deleteItem(key) {
    this.loading = true;
    this.bucket.deleteObject({Bucket: "buildevolution-myfiles", Key: this.projectName + "/" + key}, (err, response) => {
      this.keyToDelete = undefined;
      this.init();
    })
  }
    
  selectFile(event) {
    this.selectedFile = event.target.files.item(0);
  }

  getFileName(key) {
    return key.replace('timelapse/', '');
  }

  getFileSize(size) {
    let finalSize;
    let suffix;
    if (size > 1000000) {
      finalSize = size / 1000000;
      suffix = "Mo";
    } else if (size > 1000) {
      finalSize = size / 1000;
      suffix = "Ko";
    } else {
      finalSize = size;
      suffix = "O"
    }
    finalSize =  Math.round(finalSize * 100) / 100;
    return finalSize + " " + suffix;
  }

  getFileDate(date) {
    return new Date(date).toLocaleString();
  }

  // Add a helper method to get the file icon based on extension
  getFileIcon(fileName: string): string {
    if (fileName.endsWith('.mp4') || fileName.endsWith('.avi') || fileName.endsWith('.mov')) {
      return 'fas fa-file-video';
    } else if (fileName.endsWith('.jpg') || fileName.endsWith('.png') || fileName.endsWith('.gif')) {
      return 'fas fa-file-image';
    } else if (fileName.endsWith('.pdf')) {
      return 'fas fa-file-pdf';
    } else if (fileName.endsWith('.doc') || fileName.endsWith('.docx')) {
      return 'fas fa-file-word';
    } else if (fileName.endsWith('.xls') || fileName.endsWith('.xlsx')) {
      return 'fas fa-file-excel';
    }
    return 'fas fa-file';
  }

  // Add a helper method for navigation
  navigateBack() {
    if (this.breadcrumb.length > 1) {
      // Go back to parent folder
      this.breadcrumb.pop();
      this.updateFolderContents();
    }
  }
}
